import KeenSlider from 'keen-slider';

const sliderInstances = new WeakMap();
const videoState = new WeakMap();
const activeCardBySection = new WeakMap();
const iframeCardMap = new Map();
let iframeIdCounter = 0;
let messageListenerBound = false;

const setCardPlayingState = (card, isPlaying) => {
  if (!card) {
    return;
  }

  const overlay = card.querySelector('.partner-testimonials__overlay');
  const playerContainer = card.querySelector('[data-partner-testimonials-player]');

  if (overlay) {
    overlay.style.display = isPlaying ? 'none' : '';
  }

  if (playerContainer) {
    playerContainer.style.opacity = isPlaying ? '1' : '';
    playerContainer.style.pointerEvents = isPlaying ? 'auto' : '';
  }

  card.classList.toggle('is-playing', isPlaying);
};

const parseYouTubeId = (url) => {
  try {
    const parsed = new URL(url);
    if (parsed.hostname.includes('youtu.be')) {
      return parsed.pathname.replace('/', '');
    }

    if (parsed.hostname.includes('youtube.com')) {
      if (parsed.pathname.startsWith('/shorts/')) {
        return parsed.pathname.split('/shorts/')[1];
      }

      return parsed.searchParams.get('v');
    }
  } catch (error) {
    return '';
  }

  return '';
};

const parseVimeoId = (url) => {
  const match = url.match(/vimeo\.com\/(\d+)/);
  return match ? match[1] : '';
};

const pauseVideoNode = (node) => {
  if (!node) {
    return;
  }

  if (node.tagName === 'VIDEO') {
    node.pause();
    return;
  }

  if (node.tagName === 'IFRAME') {
    const provider = node.getAttribute('data-provider');
    if (provider === 'youtube') {
      node.contentWindow?.postMessage(
        JSON.stringify({
          event: 'command',
          func: 'pauseVideo',
          args: [],
        }),
        '*'
      );
      return;
    }

    if (provider === 'vimeo') {
      node.contentWindow?.postMessage({ method: 'pause' }, '*');
    }
  }
};

const playVideoNode = (node) => {
  if (!node) {
    return;
  }

  if (node.tagName === 'VIDEO') {
    node.play().catch(() => {});
    return;
  }

  if (node.tagName === 'IFRAME') {
    const provider = node.getAttribute('data-provider');
    if (provider === 'youtube') {
      node.contentWindow?.postMessage(
        JSON.stringify({
          event: 'command',
          func: 'playVideo',
          args: [],
        }),
        '*'
      );
      return;
    }

    if (provider === 'vimeo') {
      node.contentWindow?.postMessage({ method: 'play' }, '*');
    }
  }
};

const bindPlayerMessageListener = () => {
  if (messageListenerBound) {
    return;
  }

  window.addEventListener('message', (event) => {
    let payload = event.data;

    if (typeof payload === 'string') {
      try {
        payload = JSON.parse(payload);
      } catch (error) {
        return;
      }
    }

    if (!payload || typeof payload !== 'object') {
      return;
    }

    const iframeId = payload.id || payload.player_id;
    if (!iframeId || !iframeCardMap.has(iframeId)) {
      return;
    }

    const mapped = iframeCardMap.get(iframeId);
    if (!mapped || !mapped.card || !mapped.sectionRoot) {
      return;
    }

    const { card, sectionRoot } = mapped;

    // YouTube pause event
    if (payload.event === 'onStateChange' && Number(payload.info) === 2) {
      setCardPlayingState(card, false);
      if (activeCardBySection.get(sectionRoot) === card) {
        activeCardBySection.delete(sectionRoot);
      }
      return;
    }

    // Vimeo pause event
    if (payload.event === 'pause') {
      setCardPlayingState(card, false);
      if (activeCardBySection.get(sectionRoot) === card) {
        activeCardBySection.delete(sectionRoot);
      }
    }
  });

  messageListenerBound = true;
};

const createVideoNode = (url) => {
  const youtubeId = parseYouTubeId(url);
  if (youtubeId) {
    const iframe = document.createElement('iframe');
    iframe.src = `https://www.youtube.com/embed/${youtubeId}?autoplay=1&rel=0&enablejsapi=1`;
    iframe.setAttribute('data-provider', 'youtube');
    iframe.allow = 'autoplay; encrypted-media; picture-in-picture';
    iframe.allowFullscreen = true;
    iframe.title = 'Vídeo de depoimento';
    return iframe;
  }

  const vimeoId = parseVimeoId(url);
  if (vimeoId) {
    const iframe = document.createElement('iframe');
    iframe.src = `https://player.vimeo.com/video/${vimeoId}?autoplay=1`;
    iframe.setAttribute('data-provider', 'vimeo');
    iframe.allow = 'autoplay; fullscreen; picture-in-picture';
    iframe.allowFullscreen = true;
    iframe.title = 'Vídeo de depoimento';
    return iframe;
  }

  const video = document.createElement('video');
  video.src = url;
  video.controls = true;
  video.setAttribute('controlsList', 'nodownload');
  video.setAttribute('disablePictureInPicture', '');
  video.autoplay = true;
  video.playsInline = true;
  video.preload = 'metadata';
  return video;
};

const updateButtonsState = (instance, prevButton, nextButton) => {
  if (!instance || !instance.track || !instance.track.details) {
    return;
  }

  const current = instance.track.details.rel;
  const maxIndex = instance.track.details.maxIdx;

  if (prevButton) {
    const isDisabled = current <= 0;
    prevButton.disabled = isDisabled;
    prevButton.setAttribute('aria-disabled', String(isDisabled));
  }

  if (nextButton) {
    const isDisabled = current >= maxIndex;
    nextButton.disabled = isDisabled;
    nextButton.setAttribute('aria-disabled', String(isDisabled));
  }
};

const initPartnerTestimonialsSlider = (root) => {
  if (!root || sliderInstances.has(root)) {
    return;
  }

  const track = root.querySelector('.partner-testimonials__track');
  const sectionRoot = root.closest('.partner-testimonials') || root;
  const prevButton = sectionRoot.querySelector('[data-partner-testimonials-prev]');
  const nextButton = sectionRoot.querySelector('[data-partner-testimonials-next]');

  if (!track) {
    return;
  }

  const totalSlides = track.querySelectorAll('.keen-slider__slide').length;

  if (totalSlides <= 1) {
    if (prevButton) {
      prevButton.disabled = true;
      prevButton.setAttribute('aria-disabled', 'true');
    }

    if (nextButton) {
      nextButton.disabled = true;
      nextButton.setAttribute('aria-disabled', 'true');
    }

    return;
  }

  const slider = new KeenSlider(track, {
    loop: false,
    rubberband: true,
    slides: {
      perView: 'auto',
      spacing: 12,
    },
    breakpoints: {
      '(min-width: 768px)': {
        slides: {
          perView: 'auto',
          spacing: 16,
        },
      },
      '(min-width: 1024px)': {
        slides: {
          perView: 'auto',
          spacing: 20,
        },
      },
    },
    created(instance) {
      updateButtonsState(instance, prevButton, nextButton);
    },
    slideChanged(instance) {
      updateButtonsState(instance, prevButton, nextButton);
    },
    updated(instance) {
      updateButtonsState(instance, prevButton, nextButton);
    },
  });

  if (prevButton) {
    prevButton.addEventListener('click', () => slider.prev());
  }

  if (nextButton) {
    nextButton.addEventListener('click', () => slider.next());
  }

  sliderInstances.set(root, slider);
};

const initPartnerTestimonialsPlayer = (sectionRoot) => {
  bindPlayerMessageListener();

  const playButtons = sectionRoot.querySelectorAll('[data-partner-testimonials-play]');

  if (!playButtons.length) {
    return;
  }

  playButtons.forEach((button) => {
    button.addEventListener('click', () => {
      const videoUrl = button.getAttribute('data-video-url');
      if (!videoUrl) {
        return;
      }

      const card = button.closest('.partner-testimonials__card');
      const playerContainer = card ? card.querySelector('[data-partner-testimonials-player]') : null;
      if (!card || !playerContainer) {
        return;
      }

      const activeCard = activeCardBySection.get(sectionRoot);
      if (activeCard && activeCard !== card) {
        const activeVideoNode = videoState.get(activeCard);
        pauseVideoNode(activeVideoNode);
        setCardPlayingState(activeCard, false);
      }

      const existingVideoNode = videoState.get(card);

      if (existingVideoNode) {
        setCardPlayingState(card, true);
        activeCardBySection.set(sectionRoot, card);
        playVideoNode(existingVideoNode);

        return;
      }

      if (!card.classList.contains('is-playing')) {
        const videoNode = createVideoNode(videoUrl);
        playerContainer.innerHTML = '';
        playerContainer.appendChild(videoNode);
        videoState.set(card, videoNode);
        setCardPlayingState(card, true);
        activeCardBySection.set(sectionRoot, card);

        if (videoNode.tagName === 'VIDEO') {
          videoNode.addEventListener('pause', () => {
            if (!videoNode.ended) {
              setCardPlayingState(card, false);
              if (activeCardBySection.get(sectionRoot) === card) {
                activeCardBySection.delete(sectionRoot);
              }
            }
          });

          videoNode.addEventListener('play', () => {
            setCardPlayingState(card, true);
            activeCardBySection.set(sectionRoot, card);
          });

          videoNode.addEventListener('ended', () => {
            setCardPlayingState(card, false);
            if (activeCardBySection.get(sectionRoot) === card) {
              activeCardBySection.delete(sectionRoot);
            }
          });
        } else if (videoNode.tagName === 'IFRAME') {
          iframeIdCounter += 1;
          const iframeId = `partner-testimonials-iframe-${iframeIdCounter}`;
          videoNode.id = iframeId;
          videoNode.setAttribute('data-iframe-id', iframeId);
          iframeCardMap.set(iframeId, { card, sectionRoot });

          videoNode.addEventListener('load', () => {
            const provider = videoNode.getAttribute('data-provider');

            if (provider === 'youtube') {
              videoNode.contentWindow?.postMessage(
                JSON.stringify({
                  event: 'listening',
                  id: iframeId,
                }),
                '*'
              );
              videoNode.contentWindow?.postMessage(
                JSON.stringify({
                  event: 'command',
                  func: 'addEventListener',
                  args: ['onStateChange'],
                  id: iframeId,
                }),
                '*'
              );
            }

            if (provider === 'vimeo') {
              videoNode.contentWindow?.postMessage(
                {
                  method: 'addEventListener',
                  value: 'pause',
                  player_id: iframeId,
                },
                '*'
              );
            }
          });
        }
      }
    });
  });
};

const bootstrapPartnerTestimonialsSliders = () => {
  const sliders = document.querySelectorAll('[data-partner-testimonials-slider]');

  if (!sliders.length) {
    return;
  }

  sliders.forEach((sliderRoot) => {
    initPartnerTestimonialsSlider(sliderRoot);
    const sectionRoot = sliderRoot.closest('.partner-testimonials') || sliderRoot;
    initPartnerTestimonialsPlayer(sectionRoot);
  });
};

const destroyPartnerTestimonialsSliders = () => {
  const sliders = document.querySelectorAll('[data-partner-testimonials-slider]');

  sliders.forEach((sliderRoot) => {
    const instance = sliderInstances.get(sliderRoot);
    if (instance && typeof instance.destroy === 'function') {
      instance.destroy();
    }

    sliderInstances.delete(sliderRoot);
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootstrapPartnerTestimonialsSliders);
} else {
  bootstrapPartnerTestimonialsSliders();
}

window.addEventListener('beforeunload', destroyPartnerTestimonialsSliders);
